import { api, axios } from "@/api/client";

/**
 * PDF výstupy zákazky — načítanie blobu a jeho uloženie.
 *
 * Backend (`api/routers/pdf.py`) vracia binary stream + Content-Disposition
 * s filename. Ťaháme ho axiosom ako blob, takže `Content-Disposition:
 * attachment` sa vôbec neuplatní — hlavička rozhoduje len pri priamej
 * navigácii. Vďaka tomu ten istý blob vieme buď uložiť (`triggerBlobDownload`),
 * alebo ukázať v náhľade (`PdfNahladDialog`) pred uložením.
 */

export interface PdfVystup {
  blob: Blob;
  filename: string;
}

/** Text blobu cez `FileReader` — ten má prehliadač aj jsdom v testoch. */
function textBlobu(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ""));
    r.onerror = () => reject(r.error);
    r.readAsText(blob);
  });
}

/**
 * Pri `responseType: "blob"` príde aj telo chyby ako Blob, takže
 * `extractErrorMessage` nevidel `detail.message` a hlásil len „Request
 * failed with status code 409" (KOLO 163). JSON z blobu sa prečíta späť.
 */
async function chybaZBlobu(err: unknown): Promise<unknown> {
  if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
    try {
      err.response.data = JSON.parse(await textBlobu(err.response.data));
    } catch {
      // nie je to JSON — ostane pôvodná správa axiosu
    }
  }
  return err;
}

/**
 * Z ktorých variant je výstup — jedno id (otvorená záložka, ako do KOLA
 * 206), alebo zoznam (časti zákazky v jednom dokumente).
 */
export type Varianty = string | string[];

/**
 * Query pre výber variant. Jedna varianta ide ako `varianta_id` (výstup
 * presne ako doteraz), viac ako `varianta_ids` (KOLO 206).
 */
export function parametreVariant(
  varianty?: Varianty,
): Record<string, string> {
  if (varianty == null) return {};
  const ids = typeof varianty === "string" ? [varianty] : varianty;
  if (ids.length === 0) return {};
  if (ids.length === 1) return { varianta_id: ids[0] as string };
  return { varianta_ids: ids.join(",") };
}

/** `cenova_ponuka_AA6262` → `cenova_ponuka_AA6262_casti` pri viacerých. */
export function menoSuboru(zaklad: string, varianty?: Varianty): string {
  return Array.isArray(varianty) && varianty.length > 1
    ? `${zaklad}_casti`
    : zaklad;
}

/** Spoločné jadro — všetky výstupy sa líšia len cestou a parametrami. */
async function fetchPdf(
  path: string,
  params: Record<string, string | number>,
  fallbackName: string,
): Promise<PdfVystup> {
  let response;
  try {
    response = await api.get<Blob>(path, { params, responseType: "blob" });
  } catch (err) {
    throw await chybaZBlobu(err);
  }
  const disposition = response.headers["content-disposition"] as
    | string
    | undefined;
  return {
    blob: new Blob([response.data], { type: "application/pdf" }),
    filename: parseFilename(disposition) ?? fallbackName,
  };
}

/**
 * Spustí browser download blobu cez dočasný `<a download>`.
 *
 * `mime` je parameter, nie konštanta — od KOLA 71 to isté sťahovanie
 * používa aj tabuľkový export (XLSX/CSV) a natvrdo napísané
 * `application/pdf` by mu dalo nesprávny typ.
 */
export function triggerBlobDownload(
  data: Blob,
  filename: string,
  mime = "application/pdf",
): void {
  const blob = new Blob([data], { type: mime });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Cleanup blob URL (browser ho drží kým existuje referencia)
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

function parseFilename(disposition: string | undefined): string | null {
  if (!disposition) return null;
  // attachment; filename="cenova_ponuka_AA001.pdf"
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? null;
}

/** PDF cenovej ponuky (C14). Bez `zostavaIds` = všetky zostavy. */
export function fetchZakazkaPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
  zostavaIds?: string[],
): Promise<PdfVystup> {
  return fetchPdf(
    `/zakazky/${zakazkaId}/pdf`,
    {
      ...parametreVariant(varianty),
      // výber zostáv do ponuky (#74) — bez parametra = všetky
      ...(zostavaIds && zostavaIds.length
        ? { zostava_ids: zostavaIds.join(",") }
        : {}),
    },
    `${menoSuboru(`cenova_ponuka_${cisloZakazky}`, varianty)}.pdf`,
  );
}

/**
 * PDF dodacieho listu (KOLO 185) — zostavy na podpis Odovzdal / Prevzal,
 * bez cien. Výber zostáv ako pri ponuke; bez `zostavaIds` = všetky.
 */
export function fetchDodaciListPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
  zostavaIds?: string[],
): Promise<PdfVystup> {
  return fetchPdf(
    `/zakazky/${zakazkaId}/dodaci-list/pdf`,
    {
      ...parametreVariant(varianty),
      ...(zostavaIds && zostavaIds.length
        ? { zostava_ids: zostavaIds.join(",") }
        : {}),
    },
    `${menoSuboru(`dodaci_list_${cisloZakazky}`, varianty)}.pdf`,
  );
}

/** PDF nárezového plánu dĺžkových materiálov. */
export function fetchNarezovyPlanPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
  prerezMm?: number,
): Promise<PdfVystup> {
  return fetchPdf(
    `/zakazky/${zakazkaId}/narezovy-plan/pdf`,
    {
      ...parametreVariant(varianty),
      ...(prerezMm != null ? { prerez_mm: prerezMm } : {}),
    },
    `${menoSuboru(`narezovy_plan_${cisloZakazky}`, varianty)}.pdf`,
  );
}

/**
 * Nárezové plány z Cut Rite (KOLO 163) — ten istý náhľad ako ostatné
 * výstupy. Do iframe nesmie ísť adresa API: Caddy posiela `X-Frame-Options:
 * DENY` a `frame-ancestors 'none'`, takže prehliadač hlásil „rozpocty.tandem.sk
 * zamietol pripojenie"; blob URL CSP dovoľuje (`frame-src blob:`).
 */
export function fetchCutritePlanyPdf(
  zakazkaId: string,
  exportId: string,
  nazovSuboru: string,
): Promise<PdfVystup> {
  return fetchPdf(
    `/zakazky/${zakazkaId}/cutrite/${exportId}/plany.pdf`,
    {},
    `${nazovSuboru.replace(/\.csv$/i, "")}_plany.pdf`,
  );
}

/** PDF porezového plánu plošných materiálov (rozkresy tabúľ). */
export function fetchPorezPlanPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
): Promise<PdfVystup> {
  return fetchPdf(
    `/zakazky/${zakazkaId}/porez-plan/pdf`,
    parametreVariant(varianty),
    `${menoSuboru(`porez_plan_${cisloZakazky}`, varianty)}.pdf`,
  );
}

/** PDF plánu rezania laminátov (KOLO 234) — lamináty pod doskami, +20 / +20. */
export function fetchPlanLaminatovPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
): Promise<PdfVystup> {
  return fetchPdf(
    `/zakazky/${zakazkaId}/plan-laminatov/pdf`,
    parametreVariant(varianty),
    `${menoSuboru(`plan_laminatov_${cisloZakazky}`, varianty)}.pdf`,
  );
}

/** PDF kalkulačného zoznamu (interný rozpis cien dielcov). */
export function fetchKalkulaciaPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
): Promise<PdfVystup> {
  return fetchPdf(
    `/zakazky/${zakazkaId}/kalkulacia/pdf`,
    parametreVariant(varianty),
    `${menoSuboru(`kalkulacia_${cisloZakazky}`, varianty)}.pdf`,
  );
}

/** PDF súpisu materiálov podľa dodávateľa (objednávky). */
export function fetchObjednavkaPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
): Promise<PdfVystup> {
  return fetchPdf(
    `/zakazky/${zakazkaId}/objednavka/pdf`,
    parametreVariant(varianty),
    `${menoSuboru(`objednavka_${cisloZakazky}`, varianty)}.pdf`,
  );
}

/**
 * PDF súpisu nákladov materiálu (KOLO 194) — množstvá ako súpis pre
 * objednávky, nákupné ceny bez réžie a práce.
 */
export function fetchNakladyMaterialuPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
): Promise<PdfVystup> {
  return fetchPdf(
    `/zakazky/${zakazkaId}/naklady-materialu/pdf`,
    parametreVariant(varianty),
    `${menoSuboru(`naklady_materialu_${cisloZakazky}`, varianty)}.pdf`,
  );
}

/**
 * Načítaj a rovno ulož — bez náhľadu. V menu Výstupy sa už nepoužíva
 * (všetko ide cez `PdfNahladDialog`), ostáva pre priame stiahnutie.
 */
async function stiahni(vystup: Promise<PdfVystup>): Promise<void> {
  const { blob, filename } = await vystup;
  triggerBlobDownload(blob, filename);
}

export function downloadZakazkaPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
  zostavaIds?: string[],
): Promise<void> {
  return stiahni(
    fetchZakazkaPdf(zakazkaId, cisloZakazky, varianty, zostavaIds),
  );
}

export function downloadNarezovyPlanPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
  prerezMm?: number,
): Promise<void> {
  return stiahni(
    fetchNarezovyPlanPdf(zakazkaId, cisloZakazky, varianty, prerezMm),
  );
}

export function downloadPorezPlanPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
): Promise<void> {
  return stiahni(fetchPorezPlanPdf(zakazkaId, cisloZakazky, varianty));
}

export function downloadKalkulaciaPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
): Promise<void> {
  return stiahni(fetchKalkulaciaPdf(zakazkaId, cisloZakazky, varianty));
}

export function downloadObjednavkaPdf(
  zakazkaId: string,
  cisloZakazky: string,
  varianty?: Varianty,
): Promise<void> {
  return stiahni(fetchObjednavkaPdf(zakazkaId, cisloZakazky, varianty));
}

/** Cenová ponuka mimo PDF (KOLO 226) — Excel na kopírovanie, e-mail pre Outlook. */
export type FormatPonuky = "xlsx" | "eml";

const MIME_PONUKY: Record<FormatPonuky, string> = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  eml: "message/rfc822",
};

/**
 * Stiahne cenovú ponuku ako Excel alebo ako koncept e-mailu (`.eml`).
 *
 * E-mail má hlavičku `X-Unsent: 1` — Outlook ho otvorí ako nový e-mail
 * s PDF (a pri `excel` aj s Excelom) v prílohe; adresu doplní user.
 * Rovnaké parametre ako PDF ponuky, bez náhľadu (prehliadač by ich
 * v iframe nezobrazil).
 */
export async function stiahniPonuku(
  zakazkaId: string,
  cisloZakazky: string,
  format: FormatPonuky,
  varianty?: Varianty,
  zostavaIds?: string[],
  excel = false,
): Promise<void> {
  let response;
  try {
    response = await api.get<Blob>(`/zakazky/${zakazkaId}/ponuka.${format}`, {
      params: {
        ...parametreVariant(varianty),
        ...(zostavaIds && zostavaIds.length ? { zostava_ids: zostavaIds.join(",") } : {}),
        ...(format === "eml" && excel ? { excel: "true" } : {}),
      },
      responseType: "blob",
    });
  } catch (err) {
    throw await chybaZBlobu(err);
  }
  const disposition = response.headers["content-disposition"] as string | undefined;
  triggerBlobDownload(
    response.data,
    parseFilename(disposition) ??
      `${menoSuboru(`cenova_ponuka_${cisloZakazky}`, varianty)}.${format}`,
    MIME_PONUKY[format],
  );
}
